/**
 * ZIP ohne Kompression (Content-Ablage, 0155): Videos und Bilder sind schon komprimiert, ein „Speichern“-Archiv ist genauso
 * groß und braucht keine Bibliothek. Die Dateien bleiben Blobs (kein Kopieren in den Speicher); nur die Prüfsumme CRC-32 wird
 * beim Lesen berechnet. Klassisches ZIP ohne ZIP64: zusammen höchstens 4 GB, die App begrenzt ein Paket auf 500 MB.
 */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

export function crc32(data: Uint8Array, prev = 0): number {
  let c = ~prev >>> 0;
  for (let i = 0; i < data.length; i++) c = CRC_TABLE[(c ^ data[i]!) & 0xff]! ^ (c >>> 8);
  return ~c >>> 0;
}

async function crcOfBlob(b: Blob): Promise<number> {
  let crc = 0;
  const step = 8 * 1024 * 1024;
  for (let o = 0; o < b.size; o += step) crc = crc32(new Uint8Array(await b.slice(o, o + step).arrayBuffer()), crc);
  return crc;
}

/** Höchstgröße eines ZIP-Pakets im Browser; darüber lädt die App die Dateien einzeln */
export const ZIP_MAX_BYTES = 500 * 1024 * 1024;

/**
 * Namen eindeutig machen und von Pfadteilen befreien. Eindeutig auch gegen schon vergebene Namen mit Zähler („a (2).jpg“ als
 * eigene Datei) und ohne Rücksicht auf Groß- und Kleinschreibung (Windows und macOS entpacken „A.jpg“ und „a.jpg“ in dieselbe
 * Datei). Vorher konnte „a.jpg, a.jpg, a (2).jpg“ zweimal „a (2).jpg“ ergeben (Gesamtprüfung 07.10.2026).
 */
export function zipNames(names: readonly string[]): string[] {
  const used = new Set<string>();
  return names.map((raw) => {
    const clean = raw.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, "-").replace(/^\.+/, "").slice(0, 150) || "datei";
    const dot = clean.lastIndexOf(".");
    const stem = dot > 0 ? clean.slice(0, dot) : clean;
    const ext = dot > 0 ? clean.slice(dot) : "";
    let name = clean;
    for (let n = 2; used.has(name.toLowerCase()); n++) name = `${stem} (${n})${ext}`;
    used.add(name.toLowerCase());
    return name;
  });
}

export async function makeZip(files: readonly { name: string; data: Blob; date?: Date }[]): Promise<Blob> {
  const enc = new TextEncoder();
  const names = zipNames(files.map((f) => f.name));
  const parts: BlobPart[] = [];
  const central: Uint8Array<ArrayBuffer>[] = [];
  let offset = 0;
  for (let i = 0; i < files.length; i++) {
    const f = files[i]!;
    const name = enc.encode(names[i]!);
    const crc = await crcOfBlob(f.data);
    const d = f.date ?? new Date();
    const time = (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2);
    const date = ((Math.max(1980, d.getFullYear()) - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);
    lv.setUint16(6, 0x0800, true); // UTF-8-Namen
    lv.setUint16(8, 0, true); // gespeichert
    lv.setUint16(10, time, true);
    lv.setUint16(12, date, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, f.data.size, true);
    lv.setUint32(22, f.data.size, true);
    lv.setUint16(26, name.length, true);
    local.set(name, 30);
    const cd = new Uint8Array(46 + name.length);
    const cv = new DataView(cd.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, time, true);
    cv.setUint16(14, date, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, f.data.size, true);
    cv.setUint32(24, f.data.size, true);
    cv.setUint16(28, name.length, true);
    cv.setUint32(42, offset, true);
    cd.set(name, 46);
    central.push(cd);
    parts.push(local, f.data);
    offset += local.length + f.data.size;
  }
  const cdSize = central.reduce((s, c) => s + c.length, 0);
  const end = new Uint8Array(new ArrayBuffer(22));
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, files.length, true);
  ev.setUint16(10, files.length, true);
  ev.setUint32(12, cdSize, true);
  ev.setUint32(16, offset, true);
  return new Blob([...parts, ...central, end], { type: "application/zip" });
}
